import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import SEO from '../components/SEO';
import { SITE_NAME, SITE_URL } from '../constants';

type BlogPost = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  tags: string[];
  featuredImage: string;
  author?: {
    name?: string;
    email?: string;
    avatar?: string;
  } | null;
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    canonicalUrl?: string;
    keywords?: string[];
  } | null;
  readingTime: number | null;
  publishedAt: string | null;
  updatedAt?: string | null;
};

const isRemoteUrl = (value?: string | null) => /^https?:\/\//i.test(value || '');

const buildResponsiveImageProps = (src: string, widths: number[]) => {
  if (isRemoteUrl(src)) {
    return {
      src,
      srcSet: undefined as string | undefined,
    };
  }

  return {
    src: `/image?src=${encodeURIComponent(src)}&w=${widths[widths.length - 1]}`,
    srcSet: widths
      .map((width) => `/image?src=${encodeURIComponent(src)}&w=${width} ${width}w`)
      .join(', '),
  };
};

const BlogPostPage: React.FC = () => {
  const { slug } = useParams();
  const bootstrap =
    typeof window !== 'undefined'
      ? ((window as typeof window & { __BLOG_POST_BOOTSTRAP__?: BlogPost }).__BLOG_POST_BOOTSTRAP__ as BlogPost | undefined)
      : ((globalThis as typeof globalThis & { __SSR_BLOG_POST_BOOTSTRAP__?: BlogPost }).__SSR_BLOG_POST_BOOTSTRAP__ as BlogPost | undefined);
  const [post, setPost] = useState<BlogPost | null>(
    bootstrap && bootstrap.slug === slug ? bootstrap : null
  );
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!(bootstrap && bootstrap.slug === slug));

  useEffect(() => {
    let isMounted = true;
    const loadPost = async () => {
      if (!slug) {
        setError('Post not found.');
        setIsLoading(false);
        return;
      }
      if (bootstrap && bootstrap.slug === slug) {
        delete (window as typeof window & { __BLOG_POST_BOOTSTRAP__?: BlogPost }).__BLOG_POST_BOOTSTRAP__;
        setPost(bootstrap);
        setError(null);
        setIsLoading(false);
        return;
      }
      try {
        const response = await fetch(`/api/blogposts/${slug}`);
        if (!response.ok) {
          throw new Error('Request failed');
        }
        const payload = await response.json();
        if (isMounted) {
          setPost(payload);
        }
      } catch (err) {
        if (isMounted) {
          setError('Unable to load this post.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    loadPost();
    return () => {
      isMounted = false;
    };
  }, [slug]);

  const publishedLabel = useMemo(() => {
    if (!post?.publishedAt) {
      return null;
    }
    return new Date(post.publishedAt).toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  }, [post?.publishedAt]);

  const modifiedLabel = useMemo(() => {
    if (!post?.updatedAt && !post?.publishedAt) {
      return null;
    }
    return new Date(post.updatedAt || post.publishedAt || '').toISOString();
  }, [post?.publishedAt, post?.updatedAt]);

  const contentHtml = useMemo(() => {
    const raw = post?.content || '';
    const hasHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
    if (hasHtml) {
      return raw;
    }
    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    return escapeHtml(raw).replace(/\n/g, '<br />');
  }, [post?.content]);

  const canonical = post?.seo?.canonicalUrl || (post?.slug ? `${SITE_URL}/blog/${post.slug}` : undefined);
  const ogImage = post?.featuredImage || `${SITE_URL}/AviaGov.svg`;
  const authorName = post?.author?.name || 'AviaGov Editorial Team';
  const featuredImageProps = useMemo(
    () => (post?.featuredImage ? buildResponsiveImageProps(post.featuredImage, [640, 960, 1280, 1600]) : null),
    [post?.featuredImage]
  );
  const authorAvatarProps = useMemo(
    () => (post?.author?.avatar ? buildResponsiveImageProps(post.author.avatar, [128, 256]) : null),
    [post?.author?.avatar]
  );
  const schema = useMemo(() => {
    if (!post || !canonical) {
      return undefined;
    }
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.seo?.metaDescription || post.excerpt,
        articleSection: post.category || 'Aviation insights',
        mainEntityOfPage: canonical,
        url: canonical,
        image: ogImage,
        keywords: post.seo?.keywords || post.tags,
        author: {
          '@type': 'Person',
          name: authorName,
        },
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/AviaGov.svg`,
          },
        },
        datePublished: post.publishedAt,
        dateModified: post.updatedAt || post.publishedAt,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
          { '@type': 'ListItem', position: 3, name: post.title, item: canonical },
        ],
      },
    ];
  }, [authorName, canonical, ogImage, post]);

  if (isLoading) {
    return (
      <div className="animate-fade-in-up">
        <section className="py-24 px-4 max-w-4xl mx-auto">
          <p className="text-slate-400">Loading post...</p>
        </section>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="animate-fade-in-up">
        <section className="py-24 px-4 max-w-4xl mx-auto text-center">
          <p className="text-red-400 mb-6">{error || 'Post not found.'}</p>
          <Link to="/blog" className="text-accent font-bold hover:underline">
            Back to blog
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up">
      <SEO
        title={post.seo?.metaTitle || post.title}
        description={post.seo?.metaDescription || post.excerpt}
        canonical={canonical}
        keywords={post.seo?.keywords || post.tags}
        ogType="article"
        ogImage={ogImage}
        schema={schema}
      />
      <section className="py-24 px-4 max-w-4xl mx-auto">
        <Link to="/blog" className="text-accent text-xs font-bold uppercase tracking-widest">
          &larr; Back to blog
        </Link>
        <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-slate-400">
          <span className="text-accent font-bold">{post.category || 'Insights'}</span>
          {publishedLabel ? <span>{publishedLabel}</span> : null}
          {post.readingTime ? <span>{post.readingTime} min read</span> : null}
          {modifiedLabel ? <span className="sr-only">Published {modifiedLabel}</span> : null}
        </div>
        <h1 className="text-4xl md:text-5xl font-bold font-heading mt-6 mb-6">{post.title}</h1>
        <p className="text-lg text-slate-400 mb-10">{post.excerpt}</p>
        {featuredImageProps ? (
          <img
            src={featuredImageProps.src}
            srcSet={featuredImageProps.srcSet}
            sizes="100vw"
            alt={post.title}
            className="w-full rounded-[2rem] border border-white/10 mb-12"
            width={1600}
            height={900}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        ) : null}
        <div className="blog-content text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: contentHtml }} />
        {post.tags?.length ? (
          <div className="mt-12 flex flex-wrap gap-3">
            {post.tags.map((tag) => (
              <span key={tag} className="text-xs uppercase tracking-widest text-slate-400 border border-white/10 px-3 py-2 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        ) : null}
        {authorName ? (
          <div className="mt-12 p-6 border border-white/10 rounded-2xl flex flex-col md:flex-row gap-6 items-center">
            {authorAvatarProps ? (
              <img
                src={authorAvatarProps.src}
                srcSet={authorAvatarProps.srcSet}
                sizes="64px"
                alt={post.author.name}
                className="w-16 h-16 rounded-full object-cover border border-white/10"
                width={64}
                height={64}
                loading="lazy"
                decoding="async"
                referrerPolicy={isRemoteUrl(post.author.avatar) ? 'no-referrer' : undefined}
              />
            ) : null}
            <div>
              <p className="text-sm text-slate-400">Written by</p>
              <p className="text-white font-bold">{authorName}</p>
              {post.author.email ? (
                <a href={`mailto:${post.author.email}`} className="text-accent text-sm hover:underline">
                  {post.author.email}
                </a>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default BlogPostPage;
